/**
 * The ground, as a regular grid of heights in world metres.
 *
 * Everything else in the world is built on the assumption that Y = 0 is the ground, so a
 * heightfield is deliberately a *displacement* from that plane rather than an absolute
 * elevation: the terrain under the region centre is exactly zero and Kraków's 214 m above
 * the Baltic never appears anywhere. That keeps the whole existing coordinate system —
 * road layer offsets, building bases, spawn points, the camera's clearance maths — working
 * unchanged, and it keeps the numbers small enough to serialise as integers.
 *
 * Axes match the rest of the game: +X east, +Z south, +Y up.
 */

export interface HeightfieldData {
  /** Grid origin in world metres — the position of sample [0,0]. */
  originX: number;
  originZ: number;
  /** Metres between samples. */
  cell: number;
  cols: number;
  rows: number;
  /** Row-major, height in decimetres above `base`, so it serialises as small integers. */
  samples: number[];
  base: number;
}

/**
 * Grid spacing for the resampled field, metres.
 *
 * The source DEM is ~12 m per pixel, so this is a deliberate oversample: bilinear filtering
 * between two samples 12 m apart is a straight line, and a car crossing the join feels the
 * corner. Sampling at 8 m and then smoothing (below) turns those corners into curves.
 *
 * It is also where the payload stops being free. The largest region the app allows (4 km²)
 * comes to 289 × 289 samples — 261 KB of JSON, 43 KB gzipped, against a world payload that
 * is currently 0.6–0.7 MB. Dropping to a 6 m cell nearly doubles that for detail the
 * smoothing pass immediately removes again.
 */
export const DEFAULT_CELL = 8;

/**
 * Passes of a radius-1 box blur over the resampled grid. Three passes approximate a
 * Gaussian of sigma ≈ 1.4 cells, i.e. about 11 m at the default spacing.
 *
 * This exists to kill one specific artefact. Bilinear sampling of the raw DEM is only C0
 * continuous: the *height* is smooth across a source pixel boundary but the *slope* jumps,
 * and the boundaries form a 12 m lattice. A car at 40 m/s crosses one every 0.3 s, so the
 * suspension and the chase camera pick up a 3 Hz vertical chatter that reads as a rumble
 * strip laid over the entire city — worst of all on a motorway, where it should be
 * smoothest. Blurring at roughly the source pixel scale removes the lattice while leaving
 * anything a driver would call a hill: at sigma 11 m a 100 m wavelength keeps 87% of its
 * amplitude and a 200 m one keeps 96%, so Alfama's 97 m of relief survives intact.
 */
export const SMOOTHING_PASSES = 3;

/** Elevation samples are stored to this precision. Below a car's ride height, above float noise. */
const DECIMETRE = 10;

export interface RawGrid {
  originX: number;
  originZ: number;
  cell: number;
  cols: number;
  rows: number;
  /** Row-major absolute elevation in metres above sea level, `cols * rows` values. */
  heights: ArrayLike<number>;
}

/**
 * Smooths, rebases and quantises a freshly resampled grid into its serialisable form.
 *
 * The rebase is what makes the field a displacement rather than an elevation: the smoothed
 * height under the world origin becomes zero, to within the decimetre quantisation.
 */
export function buildHeightfield(grid: RawGrid): HeightfieldData {
  const { originX, originZ, cell, cols, rows } = grid;
  if (cols < 2 || rows < 2) return flatData();

  const smoothed = boxBlur(grid.heights, cols, rows, SMOOTHING_PASSES);
  const datum = bilinear(smoothed, cols, rows, (0 - originX) / cell, (0 - originZ) / cell);

  let min = Infinity;
  for (let i = 0; i < smoothed.length; i++) {
    const v = smoothed[i] - datum;
    if (v < min) min = v;
  }
  // Anchor `base` on the decimetre lattice so that identical input gives an identical
  // integer array no matter where the minimum happens to fall.
  const base = Math.floor(min * DECIMETRE) / DECIMETRE;

  const samples = new Array<number>(smoothed.length);
  for (let i = 0; i < smoothed.length; i++) {
    samples[i] = Math.round((smoothed[i] - datum - base) * DECIMETRE);
  }
  return { originX, originZ, cell, cols, rows, samples, base };
}

function flatData(): HeightfieldData {
  return { originX: 0, originZ: 0, cell: 1, cols: 0, rows: 0, samples: [], base: 0 };
}

/**
 * Separable radius-1 box blur with edge replication, repeated `passes` times.
 *
 * Edges replicate rather than wrap or darken because the grid is generated with a margin
 * past the playable area — the alternative, letting the border pull towards zero, would
 * put a lip around the world exactly where the ground plane takes over.
 */
function boxBlur(src: ArrayLike<number>, cols: number, rows: number, passes: number): Float64Array {
  const a = new Float64Array(cols * rows);
  for (let i = 0; i < a.length; i++) a[i] = src[i];
  const b = new Float64Array(a.length);

  for (let p = 0; p < passes; p++) {
    for (let r = 0; r < rows; r++) {
      const row = r * cols;
      for (let c = 0; c < cols; c++) {
        const left = a[row + (c > 0 ? c - 1 : 0)];
        const right = a[row + (c < cols - 1 ? c + 1 : cols - 1)];
        b[row + c] = (left + a[row + c] + right) / 3;
      }
    }
    for (let c = 0; c < cols; c++) {
      for (let r = 0; r < rows; r++) {
        const up = b[(r > 0 ? r - 1 : 0) * cols + c];
        const down = b[(r < rows - 1 ? r + 1 : rows - 1) * cols + c];
        a[r * cols + c] = (up + b[r * cols + c] + down) / 3;
      }
    }
  }
  return a;
}

/** Bilinear read of a raw grid in fractional grid coordinates, clamped at the edges. */
function bilinear(g: Float64Array, cols: number, rows: number, fx: number, fz: number): number {
  const cx = clamp(fx, 0, cols - 1);
  const cz = clamp(fz, 0, rows - 1);
  const ix = Math.min(Math.floor(cx), cols - 2);
  const iz = Math.min(Math.floor(cz), rows - 2);
  const tx = cx - ix;
  const tz = cz - iz;
  const o = iz * cols + ix;
  const top = g[o] + (g[o + 1] - g[o]) * tx;
  const bot = g[o + cols] + (g[o + cols + 1] - g[o + cols]) * tx;
  return top + (bot - top) * tz;
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/**
 * A queryable heightfield.
 *
 * `heightAt` is on the car's substep path — several calls per wheel per physics tick — so
 * the decimetre integers are expanded into a float array once at construction rather than
 * being unpacked on every read.
 */
export class Heightfield {
  private readonly data: HeightfieldData;
  /** Absolute world Y in metres, row-major. Empty for a flat field. */
  private readonly h: Float64Array;

  readonly minHeight: number;
  readonly maxHeight: number;
  /**
   * True when the ground is the Y = 0 plane everywhere, which is both the no-elevation
   * fallback and the genuinely level region. Callers use it to skip terrain work entirely:
   * road ribbons stay planar, the physics stops asking for a gradient.
   */
  readonly isFlat: boolean;

  private constructor(data: HeightfieldData) {
    this.data = data;
    const usable = data.cols >= 2 && data.rows >= 2 && data.samples.length === data.cols * data.rows;

    if (!usable) {
      this.h = new Float64Array(0);
      this.minHeight = 0;
      this.maxHeight = 0;
      this.isFlat = true;
      return;
    }

    this.h = new Float64Array(data.samples.length);
    let min = Infinity;
    let max = -Infinity;
    for (let i = 0; i < data.samples.length; i++) {
      const v = data.base + data.samples[i] / DECIMETRE;
      this.h[i] = v;
      if (v < min) min = v;
      if (v > max) max = v;
    }
    this.minHeight = min;
    this.maxHeight = max;
    this.isFlat = min === 0 && max === 0;
  }

  static from(data: HeightfieldData): Heightfield {
    return new Heightfield(data);
  }

  /**
   * The no-elevation ground plane, shared rather than allocated.
   *
   * The game has to be playable with no DEM at all — offline, or when the tile server is
   * down mid-lobby — and this is that world. It carries no samples, so `heightAt` returns
   * zero without touching an array and costs a branch.
   */
  static flat(): Heightfield {
    return FLAT;
  }

  toData(): HeightfieldData {
    const d = this.data;
    return {
      originX: d.originX,
      originZ: d.originZ,
      cell: d.cell,
      cols: d.cols,
      rows: d.rows,
      samples: d.samples.slice(),
      base: d.base,
    };
  }

  /** Bilinear. Clamps at the edges. */
  heightAt(x: number, z: number): number {
    if (this.h.length === 0) return 0;
    const { originX, originZ, cell, cols, rows } = this.data;
    return bilinear(this.h, cols, rows, (x - originX) / cell, (z - originZ) / cell);
  }

  /**
   * Uphill-positive gradient: the returned vector points the way the ground rises, and its
   * length is the tangent of the slope. `-dx * g` is the accelerating force on a car.
   *
   * It is the analytic derivative of the same bilinear patch `heightAt` reads, so the two
   * never disagree — a finite-difference gradient would, and the physics would then hold a
   * car on a slope it is simultaneously being pushed down.
   *
   * Off the edge of the grid the surface is constant, so the gradient goes to zero there.
   * Leaving the edge slope in place would give the car a permanent phantom pull towards the
   * horizon in a world where the height never changes again.
   */
  slopeAt(x: number, z: number): { dx: number; dz: number } {
    if (this.h.length === 0) return { dx: 0, dz: 0 };
    const { originX, originZ, cell, cols, rows } = this.data;

    const fx = (x - originX) / cell;
    const fz = (z - originZ) / cell;
    const outsideX = fx < 0 || fx > cols - 1;
    const outsideZ = fz < 0 || fz > rows - 1;

    const cx = clamp(fx, 0, cols - 1);
    const cz = clamp(fz, 0, rows - 1);
    const ix = Math.min(Math.floor(cx), cols - 2);
    const iz = Math.min(Math.floor(cz), rows - 2);
    const tx = cx - ix;
    const tz = cz - iz;

    const o = iz * cols + ix;
    const h00 = this.h[o];
    const h10 = this.h[o + 1];
    const h01 = this.h[o + cols];
    const h11 = this.h[o + cols + 1];

    return {
      dx: outsideX ? 0 : ((h10 - h00) * (1 - tz) + (h11 - h01) * tz) / cell,
      dz: outsideZ ? 0 : ((h01 - h00) * (1 - tx) + (h11 - h10) * tx) / cell,
    };
  }
}

const FLAT = Heightfield.from(flatData());
